import * as React from 'react';
import {useEffect} from 'react';
import {FieldValues, RegisterOptions, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {FormShell} from 'components/form/FormShell';
import {TextInput} from 'components/form/TextInput';
import {usePrompt} from 'helpers/useBlocker';
import {observer} from 'mobx-react-lite';
import {VehicleState} from 'store/vehicle/VehicleState';
import {CarTechDto} from '../../../../../../PRMGApiClient';
import {VehicleEditKey} from '../VehicleEditView';

interface VehicleOverviewFormProps {
  state: VehicleState;
}

export const VehicleOverviewForm: React.FC<VehicleOverviewFormProps> = observer(({state}): JSX.Element => {
  const {t} = useTranslation();

  const {vehicleId} = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isDirty},
  } = useForm<CarTechDto>({
    mode: 'onTouched',
    defaultValues: {
      enginePower: null,
      engineCapacity: null,
      acceleration: null,
      maxSpeed: null,
      mass: null,
    },
  });

  useEffect(() => {
    if (vehicleId) reset(state.item.tech);
  }, [state.item, vehicleId, reset]);

  useEffect(() => {
    state.setDirty(VehicleEditKey.overview, isDirty);
  }, [isDirty]);

  usePrompt(t('Are you sure? There are unsaved changes.'), isDirty);

  const validationRules: RegisterOptions<FieldValues> = React.useMemo(
    () => ({
      setValueAs: (value: string) => (value === '' ? undefined : Number(value)),
      validate: (value: string | null | undefined) => {
        if (value === undefined) return true;
        return Number(value) > 0 || String(t('Value must be greater than 0'));
      },
    }),
    [t]
  );

  return (
    <FormShell
      isDirty={isDirty}
      onReset={() => reset()}
      onSubmit={handleSubmit((value) => state.updateTech(value))}
      isLoading={state.isLoading}
    >
      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2">
        <TextInput
          label={t('Engine power (hp)')}
          type="number"
          {...register('enginePower', validationRules)}
          error={errors.enginePower}
        />
        <TextInput
          label={t('Engine capacity (cm3)')}
          type="number"
          {...register('engineCapacity', validationRules)}
          error={errors.engineCapacity}
        />
        <TextInput
          label={t('Acceleration (0-100 km/h)')}
          type="number"
          {...register('acceleration', validationRules)}
          error={errors.acceleration}
        />
        <TextInput
          label={t('Max speed (km/h)')}
          type="number"
          {...register('maxSpeed', validationRules)}
          error={errors.maxSpeed}
        />
        <TextInput label={t('Weight (kg)')} type="number" {...register('mass', validationRules)} error={errors.mass} />
      </div>
    </FormShell>
  );
});
